/**
 * confirmedWorkspaceCache.ts — the client half of ORG STICKINESS
 * (owner directive 2026-08-18, INCIDENT: a wedged boot silently swapped a
 * member's session identity for their personal org).
 *
 * Stores, per auth uid, the LAST CONFIRMED workspace accountId — written
 * ONLY when a strict account resolution actually succeeded (never from a
 * fallback), read by the migration gate's error/sticky paths so a transient
 * resolution failure keeps the user in the org they were last confirmed in
 * instead of inventing an identity. Cleared on sign-out and on
 * authoritative revocation (permission-denied / authoritative-null).
 *
 * Leaf module: no firebase imports (jsdom-testable in isolation).
 */

const KEY_PREFIX = 'prest0_confirmed_workspace_v1:';
/** Hygiene bound only — every successful strict resolution refreshes it. */
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export interface ConfirmedWorkspace {
  accountId: string;
  atMs: number;
}

function keyFor(uid: string): string {
  return `${KEY_PREFIX}${uid}`;
}

export function readConfirmedWorkspace(uid: string | null | undefined): ConfirmedWorkspace | null {
  const cleanUid = String(uid ?? '').trim();
  if (!cleanUid || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(keyFor(cleanUid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConfirmedWorkspace>;
    const accountId = typeof parsed.accountId === 'string' ? parsed.accountId.trim() : '';
    const atMs = typeof parsed.atMs === 'number' && Number.isFinite(parsed.atMs) ? parsed.atMs : 0;
    if (!accountId) return null;
    if (Date.now() - atMs > MAX_AGE_MS) return null;
    return { accountId, atMs };
  } catch {
    return null;
  }
}

export function writeConfirmedWorkspace(uid: string | null | undefined, accountId: string): void {
  const cleanUid = String(uid ?? '').trim();
  const cleanAccount = String(accountId ?? '').trim();
  if (!cleanUid || !cleanAccount || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(keyFor(cleanUid), JSON.stringify({ accountId: cleanAccount, atMs: Date.now() }));
  } catch {
    /* quota — the gate simply behaves as if uncached */
  }
}

export function clearConfirmedWorkspace(uid: string | null | undefined): void {
  const cleanUid = String(uid ?? '').trim();
  if (!cleanUid || typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(keyFor(cleanUid));
  } catch {
    /* ignore */
  }
}
